import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getWidgetConfig, updateWidgetConfig, toggleWidget, getThemeColors, getBillingStatus, type BillingStatus } from '../api';
import { getShopDomain } from '../shopify';
import 'datetime-web-component';
import '../widgets/hype-countdown.js';

// Form state shape — mirrors the backend config fields exactly
interface CountdownConfig {
  title: string;
  placement: string;
  display_style: string;
  timer_mode: string;
  end_date: string;
  end_behavior: string;
  end_message: string;
  redirect_url: string;
  bg_color: string;
  text_color: string;
  accent_color: string;
  background_style: string;
  gradient_end_color: string;
  days_label: string;
  hours_label: string;
  minutes_label: string;
  seconds_label: string;
  inline_message: string;
  cta_label: string;
  cta_url: string;
  cta_style: string;
  numeral_system: string;
  text_direction: string;
}

interface StylePreset {
  id: string;
  name: string;
  description: string;
  display_style: string;
  bg_color: string;
  text_color: string;
  accent_color: string;
  background_style?: string;
  gradient_end_color?: string;
}

const STYLE_PRESETS: StylePreset[] = [
  {
    id: 'classic-dark',
    name: 'Classic Dark',
    description: 'Dark background, white text, orange accent',
    display_style: 'card',
    bg_color: '#111111',
    text_color: '#ffffff',
    accent_color: '#ff4500',
  },
  {
    id: 'clean-light',
    name: 'Clean Light',
    description: 'White background, dark text, blue accent',
    display_style: 'card',
    bg_color: '#ffffff',
    text_color: '#1a1a1a',
    accent_color: '#005bd3',
  },
  {
    id: 'bold-red',
    name: 'Bold Red',
    description: 'Red background, white text, yellow accent',
    display_style: 'card',
    bg_color: '#dc2626',
    text_color: '#ffffff',
    accent_color: '#facc15',
  },
  {
    id: 'elegant-gold',
    name: 'Elegant Gold',
    description: 'Near-black background, gold text and accent',
    display_style: 'minimal',
    bg_color: '#0a0a0a',
    text_color: '#d4a853',
    accent_color: '#d4a853',
  },
  {
    id: 'holiday',
    name: 'Holiday',
    description: 'Green background, white text, red accent',
    display_style: 'card',
    bg_color: '#166534',
    text_color: '#ffffff',
    accent_color: '#dc2626',
  },
  {
    id: 'bfcm',
    name: 'BFCM',
    description: 'Black background, white text, green accent',
    display_style: 'flip',
    bg_color: '#000000',
    text_color: '#ffffff',
    accent_color: '#22c55e',
  },
  {
    id: 'cart-urgency',
    name: 'Cart Urgency',
    description: 'Inline text timer, white bg, red accent for cart pages',
    display_style: 'inline_text',
    bg_color: '#ffffff',
    text_color: '#1a1a1a',
    accent_color: '#dc2626',
  },
];

function oneWeekFromNow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString();
}

/** Format ISO string as friendly local date, e.g. "Feb 28, 2026 at 4:32 PM (PST)" */
function formatLocalDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const tz = d.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
  return `${date} at ${time} (${tz})`;
}

const DEFAULTS: CountdownConfig = {
  title: 'Sale ends in',
  placement: 'inline',
  display_style: 'card',
  timer_mode: 'fixed',
  end_date: oneWeekFromNow(),
  end_behavior: 'hide',
  end_message: 'This offer has ended.',
  redirect_url: '',
  bg_color: '#111111',
  text_color: '#ffffff',
  accent_color: '#ff4500',
  background_style: 'solid',
  gradient_end_color: '#333333',
  days_label: 'days',
  hours_label: 'hrs',
  minutes_label: 'min',
  seconds_label: 'sec',
  inline_message: 'Hurry! Sale ends in {timer}',
  cta_label: '',
  cta_url: '',
  cta_style: 'solid',
  numeral_system: 'western',
  text_direction: '',
};

type PreviewState = 'running' | 'near-expiry' | 'expired-message' | 'expired-hidden';

@customElement('hype-countdown-page')
export class CountdownPage extends LitElement {
  override createRenderRoot() { return this; }

  // Loading / saving states
  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _loadError = '';
  @state() private _saveSuccess = false;
  @state() private _saveError = '';
  @state() private _saveFlash = false;

  private _flashTimer: ReturnType<typeof setTimeout> | undefined;

  // Widget enabled toggle
  @state() private _enabled = false;

  // Preview state override — does not affect saved config
  @state() private _previewState: PreviewState = 'running';

  // Ethics banner
  @state() private _ethicsBannerDismissed = false;

  // Form fields
  @state() private _title = DEFAULTS.title;
  @state() private _placement = DEFAULTS.placement;
  @state() private _display_style = DEFAULTS.display_style;
  @state() private _timer_mode = DEFAULTS.timer_mode;
  @state() private _end_date = DEFAULTS.end_date;
  @state() private _end_behavior = DEFAULTS.end_behavior;
  @state() private _end_message = DEFAULTS.end_message;
  @state() private _redirect_url = DEFAULTS.redirect_url;
  @state() private _bg_color = DEFAULTS.bg_color;
  @state() private _text_color = DEFAULTS.text_color;
  @state() private _accent_color = DEFAULTS.accent_color;
  @state() private _background_style = DEFAULTS.background_style;
  @state() private _gradient_end_color = DEFAULTS.gradient_end_color;
  @state() private _days_label = DEFAULTS.days_label;
  @state() private _hours_label = DEFAULTS.hours_label;
  @state() private _minutes_label = DEFAULTS.minutes_label;
  @state() private _seconds_label = DEFAULTS.seconds_label;
  @state() private _inline_message = DEFAULTS.inline_message;
  @state() private _cta_label = DEFAULTS.cta_label;
  @state() private _cta_url = DEFAULTS.cta_url;
  @state() private _cta_style = DEFAULTS.cta_style;
  @state() private _numeral_system = DEFAULTS.numeral_system;
  @state() private _text_direction = DEFAULTS.text_direction;

  // Custom CSS (Pro plan)
  @state() private _custom_css = '';
  @state() private _billing: BillingStatus | null = null;

  private _dtcPatched = false;

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadConfig();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this._flashTimer);
  }

  override updated(): void {
    // Shopify admin's CSS reset kills number input spinners. Inject a
    // counter-reset into the datetime-web-component's open shadow root.
    if (this._dtcPatched) return;
    const dtc = this.querySelector('datetime-web-component');
    if (!dtc?.shadowRoot) return;
    this._dtcPatched = true;
    const fix = document.createElement('style');
    fix.textContent = `
      input[type="number"] {
        -webkit-appearance: auto !important;
        -moz-appearance: auto !important;
        appearance: auto !important;
      }
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: inner-spin-button !important;
        opacity: 1 !important;
        height: auto !important;
      }
    `;
    dtc.shadowRoot.appendChild(fix);
  }

  private async _loadConfig(): Promise<void> {
    this._loading = true;
    this._loadError = '';
    try {
      const [widget, billing] = await Promise.all([
        getWidgetConfig('countdown'),
        getBillingStatus().catch(() => null),
      ]);
      this._billing = billing;
      this._enabled = widget.enabled;
      this._custom_css = widget.custom_css ?? '';
      const c = widget.config as Partial<CountdownConfig>;
      this._title         = (c.title         ?? DEFAULTS.title)         as string;
      this._placement     = (c.placement     ?? DEFAULTS.placement)     as string;
      this._display_style = (c.display_style ?? DEFAULTS.display_style) as string;
      this._timer_mode    = (c.timer_mode    ?? DEFAULTS.timer_mode)    as string;
      this._end_date    = (c.end_date    || DEFAULTS.end_date)    as string;
      this._end_behavior= (c.end_behavior?? DEFAULTS.end_behavior)as string;
      this._end_message = (c.end_message ?? DEFAULTS.end_message) as string;
      this._redirect_url= (c.redirect_url?? DEFAULTS.redirect_url)as string;
      this._bg_color           = (c.bg_color           ?? DEFAULTS.bg_color)           as string;
      this._text_color         = (c.text_color         ?? DEFAULTS.text_color)         as string;
      this._accent_color       = (c.accent_color       ?? DEFAULTS.accent_color)       as string;
      this._background_style   = (c.background_style   ?? DEFAULTS.background_style)   as string;
      this._gradient_end_color = (c.gradient_end_color ?? DEFAULTS.gradient_end_color) as string;
      this._days_label         = (c.days_label         ?? DEFAULTS.days_label)         as string;
      this._hours_label        = (c.hours_label        ?? DEFAULTS.hours_label)        as string;
      this._minutes_label      = (c.minutes_label      ?? DEFAULTS.minutes_label)      as string;
      this._seconds_label      = (c.seconds_label      ?? DEFAULTS.seconds_label)      as string;
      this._inline_message     = (c.inline_message     ?? DEFAULTS.inline_message)     as string;
      this._cta_label          = (c.cta_label          ?? DEFAULTS.cta_label)          as string;
      this._cta_url            = (c.cta_url            ?? DEFAULTS.cta_url)            as string;
      this._cta_style          = (c.cta_style          ?? DEFAULTS.cta_style)          as string;
      this._numeral_system     = (c.numeral_system     ?? DEFAULTS.numeral_system)     as string;
      this._text_direction     = (c.text_direction     ?? DEFAULTS.text_direction)     as string;

      // If colors are still at defaults, try to load theme colors as smart defaults.
      // Only applies when the merchant has never customized colors (config came back
      // as defaults, meaning no saved config row existed yet).
      if (this._bg_color === DEFAULTS.bg_color &&
          this._text_color === DEFAULTS.text_color &&
          this._accent_color === DEFAULTS.accent_color) {
        try {
          const themeColors = await getThemeColors();
          if (themeColors.bg_color) this._bg_color = themeColors.bg_color;
          if (themeColors.text_color) this._text_color = themeColors.text_color;
          if (themeColors.accent_color) this._accent_color = themeColors.accent_color;
        } catch {
          // Silently ignore — theme colors are a nice-to-have
        }
      }
    } catch (err) {
      this._loadError = err instanceof Error ? err.message : 'Failed to load configuration.';
    } finally {
      this._loading = false;
    }
  }

  private async _handleToggle(enabled: boolean): Promise<void> {
    this._enabled = enabled;
    try {
      const config: Record<string, unknown> = {
        title: this._title,
        placement: this._placement,
        display_style: this._display_style,
        timer_mode: this._timer_mode,
        end_date: this._end_date,
        end_behavior: this._end_behavior,
        end_message: this._end_message,
        redirect_url: this._redirect_url,
        bg_color: this._bg_color,
        text_color: this._text_color,
        accent_color: this._accent_color,
        background_style: this._background_style,
        gradient_end_color: this._gradient_end_color,
        days_label: this._days_label,
        hours_label: this._hours_label,
        minutes_label: this._minutes_label,
        seconds_label: this._seconds_label,
        inline_message: this._inline_message,
        cta_label: this._cta_label,
        cta_url: this._cta_url,
        cta_style: this._cta_style,
        numeral_system: this._numeral_system,
        text_direction: this._text_direction,
      };
      await toggleWidget('countdown', enabled, config);
    } catch (err) {
      // Revert optimistic update on failure
      this._enabled = !enabled;
      this._saveError = err instanceof Error ? err.message : 'Failed to toggle widget.';
      this._saveSuccess = false;
    }
  }

  private async _handleSave(): Promise<void> {
    this._saving = true;
    this._saveSuccess = false;
    this._saveError = '';
    try {
      const config: CountdownConfig = {
        title: this._title,
        placement: this._placement,
        display_style: this._display_style,
        timer_mode: this._timer_mode,
        end_date: this._end_date,
        end_behavior: this._end_behavior,
        end_message: this._end_message,
        redirect_url: this._redirect_url,
        bg_color: this._bg_color,
        text_color: this._text_color,
        accent_color: this._accent_color,
        background_style: this._background_style,
        gradient_end_color: this._gradient_end_color,
        days_label: this._days_label,
        hours_label: this._hours_label,
        minutes_label: this._minutes_label,
        seconds_label: this._seconds_label,
        inline_message: this._inline_message,
        cta_label: this._cta_label,
        cta_url: this._cta_url,
        cta_style: this._cta_style,
        numeral_system: this._numeral_system,
        text_direction: this._text_direction,
      };
      await updateWidgetConfig('countdown', config as unknown as Record<string, unknown>, this._custom_css);
      this._saveSuccess = true;
      // Trigger brief success flash on save button + preview pulse
      clearTimeout(this._flashTimer);
      this._saveFlash = true;
      this._flashTimer = setTimeout(() => { this._saveFlash = false; }, 1500);
    } catch (err) {
      this._saveError = err instanceof Error ? err.message : 'Failed to save configuration.';
    } finally {
      this._saving = false;
    }
  }

  private _applyPreset(preset: StylePreset): void {
    this._display_style = preset.display_style;
    this._bg_color = preset.bg_color;
    this._text_color = preset.text_color;
    this._accent_color = preset.accent_color;
    this._background_style = preset.background_style ?? 'solid';
    this._gradient_end_color = preset.gradient_end_color ?? '#333333';
  }

  private _handleColor(key: string, value: string): void {
    if (key === 'bg_color')            this._bg_color            = value;
    if (key === 'text_color')          this._text_color          = value;
    if (key === 'accent_color')        this._accent_color        = value;
    if (key === 'gradient_end_color')  this._gradient_end_color  = value;
  }

  /** Returns the preview widget (or placeholder) based on _previewState. */
  private _renderPreview() {
    if (this._previewState === 'expired-hidden') {
      return html`
        <div class="preview-expired-placeholder">
          Timer hidden when expired
        </div>
      `;
    }

    if (this._previewState === 'expired-message') {
      // A past date guarantees the widget enters expired state immediately.
      const pastDate = new Date(Date.now() - 1000).toISOString();
      return html`
        <hype-countdown
          title=${this._title}
          end-date=${pastDate}
          timer-mode="fixed"
          end-behavior="message"
          end-message=${this._end_message || 'This offer has ended.'}
          redirect-url=${this._redirect_url}
          bg-color=${this._bg_color}
          text-color=${this._text_color}
          accent-color=${this._accent_color}
          placement="inline"
          display-style=${this._display_style}
          background-style=${this._background_style}
          gradient-end-color=${this._gradient_end_color}
          days-label=${this._days_label}
          hours-label=${this._hours_label}
          minutes-label=${this._minutes_label}
          seconds-label=${this._seconds_label}
          inline-message=${this._inline_message}
          cta-label=${this._cta_label}
          cta-url=${this._cta_url}
          cta-style=${this._cta_style}
          numeral-system=${this._numeral_system}
          dir=${this._text_direction}
        ></hype-countdown>
      `;
    }

    if (this._previewState === 'near-expiry') {
      // 2 minutes from now triggers the "warning" urgency state (≤30min).
      const nearDate = new Date(Date.now() + 2 * 60 * 1000).toISOString();
      return html`
        <hype-countdown
          title=${this._title}
          end-date=${nearDate}
          timer-mode="fixed"
          end-behavior=${this._end_behavior}
          end-message=${this._end_message}
          redirect-url=${this._redirect_url}
          bg-color=${this._bg_color}
          text-color=${this._text_color}
          accent-color=${this._accent_color}
          placement="inline"
          display-style=${this._display_style}
          background-style=${this._background_style}
          gradient-end-color=${this._gradient_end_color}
          days-label=${this._days_label}
          hours-label=${this._hours_label}
          minutes-label=${this._minutes_label}
          seconds-label=${this._seconds_label}
          inline-message=${this._inline_message}
          cta-label=${this._cta_label}
          cta-url=${this._cta_url}
          cta-style=${this._cta_style}
          numeral-system=${this._numeral_system}
          dir=${this._text_direction}
        ></hype-countdown>
      `;
    }

    // 'running' — use real form state
    return html`
      <hype-countdown
        title=${this._title}
        end-date=${this._end_date}
        timer-mode=${this._timer_mode}
        end-behavior=${this._end_behavior}
        end-message=${this._end_message}
        redirect-url=${this._redirect_url}
        bg-color=${this._bg_color}
        text-color=${this._text_color}
        accent-color=${this._accent_color}
        placement="inline"
        display-style=${this._display_style}
        background-style=${this._background_style}
        gradient-end-color=${this._gradient_end_color}
        days-label=${this._days_label}
        hours-label=${this._hours_label}
        minutes-label=${this._minutes_label}
        seconds-label=${this._seconds_label}
        inline-message=${this._inline_message}
        cta-label=${this._cta_label}
        cta-url=${this._cta_url}
        cta-style=${this._cta_style}
        numeral-system=${this._numeral_system}
        dir=${this._text_direction}
      ></hype-countdown>
    `;
  }

  override render() {
    if (this._loading) {
      return html`<s-spinner></s-spinner>`;
    }

    if (this._loadError) {
      return html`
        <s-page heading="Countdown Timer">
          <s-banner tone="critical">
            <strong>Failed to load configuration</strong><br />
            ${this._loadError}
          </s-banner>
          <s-button @click=${() => void this._loadConfig()}>Retry</s-button>
        </s-page>
      `;
    }

    return html`
      <style>
        hype-countdown-page .preview-layout {
          display: grid;
          grid-template-columns: 55fr 45fr;
          gap: 24px;
          align-items: start;
        }
        hype-countdown-page .config-column {
          display: flex;
          flex-direction: column;
          gap: var(--p-space-base, 16px);
        }
        hype-countdown-page .preview-panel {
          position: sticky;
          top: 80px;
        }
        hype-countdown-page .preview-frame {
          border: 1px solid #e3e3e3;
          border-radius: 12px;
          background: #fafafa;
          padding: 24px;
          overflow: hidden;
        }
        hype-countdown-page .save-btn-success {
          --p-color-bg-fill-success: #008060;
          background-color: #008060 !important;
          border-color: #008060 !important;
          transition: background-color 0.2s, border-color 0.2s;
        }
        @keyframes hype-preview-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(0,128,96,0.5); border-color: #008060; }
          60%  { box-shadow: 0 0 0 6px rgba(0,128,96,0); border-color: #008060; }
          100% { box-shadow: 0 0 0 0 rgba(0,128,96,0); border-color: #e3e3e3; }
        }
        hype-countdown-page .preview-frame.preview-pulse {
          animation: hype-preview-pulse 1s ease-out forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          hype-countdown-page .save-btn-success {
            transition: none;
          }
          hype-countdown-page .preview-frame.preview-pulse {
            animation: none;
            border-color: #008060;
          }
        }
        hype-countdown-page .preview-state-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 12px;
        }
        hype-countdown-page .preview-expired-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 52px;
          border: 2px dashed #c9cccf;
          border-radius: 8px;
          background: #f6f6f7;
          color: #8c9196;
          font-size: 0.85rem;
          font-style: italic;
        }
        hype-countdown-page .preset-grid {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 4px;
        }
        hype-countdown-page .preset-card {
          width: 120px;
          cursor: pointer;
          border: 2px solid #e3e3e3;
          border-radius: 8px;
          padding: 10px;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          gap: 6px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        hype-countdown-page .preset-card:hover {
          border-color: #b5b5b5;
          box-shadow: 0 2px 6px rgba(0,0,0,0.10);
        }
        hype-countdown-page .preset-card.active {
          border-color: #005bd3;
          box-shadow: 0 0 0 1px #005bd3;
          background: #f0f7ff;
        }
        hype-countdown-page .preset-card-name {
          font-size: 12px;
          font-weight: 600;
          color: #303030;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        hype-countdown-page .preset-swatches {
          display: flex;
          flex-direction: row;
          gap: 4px;
          align-items: center;
        }
        hype-countdown-page .preset-swatch {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid rgba(0,0,0,0.15);
          flex-shrink: 0;
        }
        hype-countdown-page .preset-card-style {
          font-size: 10px;
          color: #8c9196;
          text-transform: capitalize;
        }
        @media (max-width: 768px) {
          hype-countdown-page .preview-layout {
            grid-template-columns: 1fr;
          }
          hype-countdown-page .preview-panel {
            position: static;
            order: -1;
          }
        }
        hype-countdown-page datetime-web-component {
          /* Text & Font */
          --font-family: -apple-system, BlinkMacSystemFont, 'San Francisco', 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
          --font-size: 13px;
          --text-color: #303030;
          --muted-text-color: #616161;
          --disabled-text-color: #b5b5b5;
          --focus-color: #005bd3;

          /* Dropdown / container */
          --background: #ffffff;
          --box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          --border-color: #c9cccf;
          --border-width: 1px;
          --border-radius: 8px;
          --padding: 12px;

          /* Date cells */
          --cell-width: 32px;
          --cell-height: 32px;
          --cell-border-radius: 6px;
          --cell-vertical-margin: 1px;
          --cell-horizontal-margin: 1px;
          --selected-cell-background: #005bd3;
          --selected-cell-font-weight: 600;
          --hover-cell-background: #f1f1f1;

          /* Inputs (month/year/time selectors) */
          --input-font-size: 16px;
          --input-font-weight: 600;
          --input-border-radius: 6px;
          --input-hover-background: #f1f1f1;
        }
        hype-countdown-page datetime-web-component input[slot="input"] {
          font-family: -apple-system, BlinkMacSystemFont, 'San Francisco', 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
          font-size: 13px;
          padding: 6px 12px;
          border: 1px solid #c9cccf;
          border-radius: 8px;
          color: #303030;
          background: #ffffff;
          width: 100%;
          box-sizing: border-box;
          cursor: pointer;
          outline: none;
        }
        hype-countdown-page datetime-web-component input[slot="input"]:focus {
          border-color: #005bd3;
          box-shadow: 0 0 0 1px #005bd3;
        }
      </style>
      <s-page heading="Countdown Timer">
        <s-button
          slot="primary-action"
          variant="primary"
          class="${this._saveFlash ? 'save-btn-success' : ''}"
          ?loading=${this._saving}
          @click=${() => void this._handleSave()}
        >${this._saveFlash ? '✓ Saved' : 'Save'}</s-button>
        ${(() => { const shop = getShopDomain(); return shop ? html`<s-button slot="secondary-actions" variant="tertiary" @click=${() => window.open(`https://${shop}`, '_blank')} aria-label="Preview widget on your live store">Preview on store</s-button>` : ''; })()}

        ${this._saveSuccess ? html`
          <s-banner
            tone="success"
            dismissible
            @dismiss=${() => { this._saveSuccess = false; }}
          >Configuration saved successfully.</s-banner>
        ` : ''}

        ${this._saveError ? html`
          <s-banner
            tone="critical"
            dismissible
            @dismiss=${() => { this._saveError = ''; }}
          >${this._saveError}</s-banner>
        ` : ''}

        ${!this._ethicsBannerDismissed ? html`
          <s-banner tone="info" dismissible @dismiss=${() => { this._ethicsBannerDismissed = true; }}>
            <strong>Authentic urgency converts better.</strong> Real deadlines (flash sales, limited inventory, event launches)
            outperform manufactured countdown timers. Pair timers with genuine offers for the best results and customer trust.
          </s-banner>
        ` : ''}

        <div class="preview-layout">
          <div class="config-column">
            <s-section heading="Quick Start" class="${this._enabled ? '' : 'disabled-overlay'}">
              <div class="preset-grid">
                ${STYLE_PRESETS.map(preset => {
                  const isActive =
                    preset.display_style === this._display_style &&
                    preset.bg_color === this._bg_color &&
                    preset.text_color === this._text_color &&
                    preset.accent_color === this._accent_color &&
                    (preset.background_style ?? 'solid') === this._background_style &&
                    (preset.gradient_end_color ?? '#333333') === this._gradient_end_color;
                  return html`
                    <div
                      class="preset-card ${isActive ? 'active' : ''}"
                      role="button"
                      tabindex="0"
                      @click=${() => this._applyPreset(preset)}
                      @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._applyPreset(preset); } }}
                      title=${preset.description}
                    >
                      <div class="preset-card-name">${preset.name}</div>
                      <div class="preset-swatches">
                        <div class="preset-swatch" style="background:${preset.bg_color}"></div>
                        <div class="preset-swatch" style="background:${preset.text_color}"></div>
                        <div class="preset-swatch" style="background:${preset.accent_color}"></div>
                      </div>
                      <div class="preset-card-style">${preset.display_style}</div>
                    </div>
                  `;
                })}
              </div>
            </s-section>

            <s-section heading="Display">
              <s-stack gap="base">
                <s-checkbox
                  label="Enable widget"
                  ?checked=${this._enabled}
                  @change=${(e: Event) => void this._handleToggle((e.currentTarget as HTMLInputElement).checked)}
                ></s-checkbox>

                <div class="${this._enabled ? '' : 'disabled-overlay'}">
                  <s-stack gap="base">
                    <s-text-field
                      label="Timer label"
                      help-text="Text shown above or beside the countdown digits."
                      value=${this._title}
                      @change=${(e: Event) => { this._title = (e.currentTarget as HTMLInputElement).value; }}
                    ></s-text-field>

                    <s-select
                      label="Placement"
                      value=${this._placement}
                      @change=${(e: Event) => { this._placement = (e.currentTarget as HTMLInputElement).value; }}
                    >
                      <s-option value="inline">Inline (where dropped)</s-option>
                      <s-option value="sticky_top">Sticky — top of page</s-option>
                      <s-option value="sticky_bottom">Sticky — bottom of page</s-option>
                    </s-select>

                    <s-select
                      label="Display style"
                      value=${this._display_style}
                      @change=${(e: Event) => { this._display_style = (e.currentTarget as HTMLInputElement).value; }}
                    >
                      <s-option value="card">Card</s-option>
                      <s-option value="minimal">Minimal</s-option>
                      <s-option value="flip">Flip clock</s-option>
                      <s-option value="inline_text">Inline text</s-option>
                    </s-select>

                    ${this._display_style === 'inline_text' ? html`
                      <s-text-field
                        label="Inline message"
                        help-text="Use {timer} as placeholder for countdown digits. Translate for non-English stores."
                        value=${this._inline_message}
                        @change=${(e: Event) => { this._inline_message = (e.currentTarget as HTMLInputElement).value; }}
                      ></s-text-field>
                    ` : ''}

                    ${this._display_style !== 'inline_text' ? html`
                      <s-text-field
                        label="Days label"
                        help-text="Text below the days digit. Change for non-English stores."
                        value=${this._days_label}
                        @change=${(e: Event) => { this._days_label = (e.currentTarget as HTMLInputElement).value; }}
                      ></s-text-field>

                      <s-text-field
                        label="Hours label"
                        help-text="Text below the hours digit."
                        value=${this._hours_label}
                        @change=${(e: Event) => { this._hours_label = (e.currentTarget as HTMLInputElement).value; }}
                      ></s-text-field>

                      <s-text-field
                        label="Minutes label"
                        help-text="Text below the minutes digit."
                        value=${this._minutes_label}
                        @change=${(e: Event) => { this._minutes_label = (e.currentTarget as HTMLInputElement).value; }}
                      ></s-text-field>

                      <s-text-field
                        label="Seconds label"
                        help-text="Text below the seconds digit."
                        value=${this._seconds_label}
                        @change=${(e: Event) => { this._seconds_label = (e.currentTarget as HTMLInputElement).value; }}
                      ></s-text-field>
                    ` : ''}
                  </s-stack>
                </div>
              </s-stack>
            </s-section>

            <s-section heading="Timer Behavior" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-select
                  label="Timer mode"
                  help-text="Fixed = real deadline. Session-based timers reset per visitor — use with care as customers may notice."
                  value=${this._timer_mode}
                  @change=${(e: Event) => { this._timer_mode = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="fixed">Fixed end date / time</s-option>
                  <s-option value="daily">Recurring daily</s-option>
                  <s-option value="session">Session-based (resets per visitor)</s-option>
                </s-select>

                ${this._timer_mode === 'fixed' ? html`
                  <s-stack gap="base">
                    <s-text variant="bodyMd" fontWeight="semibold">End date and time</s-text>
                    <datetime-web-component
                      value=${this._end_date}
                      @input=${(e: CustomEvent) => {
                        const val = e.detail?.value ?? (e.target as any).value;
                        this._end_date = val;
                        const slottedInput = (e.currentTarget as HTMLElement).querySelector('input[slot="input"]') as HTMLInputElement | null;
                        if (slottedInput) slottedInput.value = formatLocalDate(val);
                      }}
                    ><input slot="input" type="text" .value=${formatLocalDate(this._end_date)} placeholder="Select date and time" readonly /></datetime-web-component>
                    <s-text tone="subdued" variant="bodySm">The timer counts down to this moment.</s-text>
                  </s-stack>
                ` : ''}

                <s-select
                  label="When timer reaches zero"
                  value=${this._end_behavior}
                  @change=${(e: Event) => { this._end_behavior = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="hide">Hide the timer</s-option>
                  <s-option value="message">Show a message</s-option>
                  <s-option value="redirect">Redirect to URL</s-option>
                </s-select>

                ${this._end_behavior === 'message' ? html`
                  <s-text-field
                    label="End message"
                    help-text="Displayed when the timer reaches zero."
                    value=${this._end_message}
                    @change=${(e: Event) => { this._end_message = (e.currentTarget as HTMLInputElement).value; }}
                  ></s-text-field>
                ` : ''}

                ${this._end_behavior === 'redirect' ? html`
                  <s-text-field
                    label="Redirect URL"
                    type="url"
                    placeholder="https://example.com/sale"
                    help-text="Visitors will be redirected here when the timer ends."
                    value=${this._redirect_url}
                    @change=${(e: Event) => { this._redirect_url = (e.currentTarget as HTMLInputElement).value; }}
                  ></s-text-field>
                ` : ''}
              </s-stack>
            </s-section>

            <s-section heading="Colors" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-select
                  label="Background style"
                  value=${this._background_style}
                  @change=${(e: Event) => { this._background_style = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="solid">Solid</s-option>
                  <s-option value="gradient">Gradient</s-option>
                  <s-option value="glass">Glassmorphism</s-option>
                </s-select>

                <s-stack gap="base">
                  <s-text>${this._background_style === 'gradient' ? 'Gradient start color' : 'Background'}</s-text>
                  <s-color-picker
                    value=${this._bg_color}
                    @input=${(e: Event) => this._handleColor('bg_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>

                ${this._background_style === 'gradient' ? html`
                  <s-stack gap="base">
                    <s-text>Gradient end color</s-text>
                    <s-color-picker
                      value=${this._gradient_end_color}
                      @input=${(e: Event) => this._handleColor('gradient_end_color', (e.currentTarget as HTMLInputElement).value)}
                    ></s-color-picker>
                  </s-stack>
                ` : ''}

                <s-stack gap="base">
                  <s-text>Text</s-text>
                  <s-color-picker
                    value=${this._text_color}
                    @input=${(e: Event) => this._handleColor('text_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>
                <s-stack gap="base">
                  <s-text>Accent / digits</s-text>
                  <s-color-picker
                    value=${this._accent_color}
                    @input=${(e: Event) => this._handleColor('accent_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>
              </s-stack>
            </s-section>

            <s-section heading="Call to Action" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-text-field
                  label="Button label"
                  help-text="Leave empty to hide the button. Translate for non-English stores."
                  value=${this._cta_label}
                  @change=${(e: Event) => { this._cta_label = (e.currentTarget as HTMLInputElement).value; }}
                ></s-text-field>

                ${this._cta_label ? html`
                  <s-text-field
                    label="Button URL"
                    type="url"
                    placeholder="https://example.com/shop"
                    value=${this._cta_url}
                    @change=${(e: Event) => { this._cta_url = (e.currentTarget as HTMLInputElement).value; }}
                  ></s-text-field>

                  <s-select
                    label="Button style"
                    value=${this._cta_style}
                    @change=${(e: Event) => { this._cta_style = (e.currentTarget as HTMLInputElement).value; }}
                  >
                    <s-option value="solid">Solid (filled)</s-option>
                    <s-option value="outline">Outline</s-option>
                    <s-option value="link">Link (text only)</s-option>
                  </s-select>
                ` : ''}
              </s-stack>
            </s-section>

            <s-section heading="Localization" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-select
                  label="Numeral system"
                  value=${this._numeral_system}
                  @change=${(e: Event) => { this._numeral_system = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="western">Western (0-9)</s-option>
                  <s-option value="arabic">Arabic-Indic (&#x0660;-&#x0669;)</s-option>
                  <s-option value="devanagari">Devanagari (&#x0966;-&#x096F;)</s-option>
                  <s-option value="auto">Auto-detect from store locale</s-option>
                </s-select>

                <s-select
                  label="Text direction"
                  value=${this._text_direction}
                  @change=${(e: Event) => { this._text_direction = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="">Auto-detect from store locale</s-option>
                  <s-option value="ltr">Left to right</s-option>
                  <s-option value="rtl">Right to left</s-option>
                </s-select>
              </s-stack>
            </s-section>

            <s-section heading="Custom CSS" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                ${this._billing && !(this._billing.features as Record<string, boolean>).custom_css ? html`
                  <s-banner tone="info">
                    Custom CSS is available on the Pro plan.
                    <a href="/billing">Upgrade to Pro</a> to inject custom styles.
                  </s-banner>
                ` : ''}
                <textarea
                  rows="6"
                  placeholder=".bar { border-radius: 8px; }"
                  .value=${this._custom_css}
                  ?disabled=${!!(this._billing && !(this._billing.features as Record<string, boolean>).custom_css)}
                  @input=${(e: Event) => { this._custom_css = (e.currentTarget as HTMLTextAreaElement).value; }}
                  style="width:100%;font-family:monospace;font-size:13px;padding:8px;border:1px solid #d2d5d8;border-radius:8px;resize:vertical;box-sizing:border-box;"
                ></textarea>
                <s-text tone="subdued" variant="bodySm">
                  CSS is scoped to this widget's Shadow DOM. Use class names like .bar, .digit, .label.
                </s-text>
              </s-stack>
            </s-section>
          </div>

          <div class="preview-panel">
            <s-section heading="Preview">
              <div class="preview-state-buttons">
                <s-button
                  size="slim"
                  variant=${this._previewState === 'running' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'running'; }}
                >Running</s-button>
                <s-button
                  size="slim"
                  variant=${this._previewState === 'near-expiry' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'near-expiry'; }}
                >Near expiry</s-button>
                <s-button
                  size="slim"
                  variant=${this._previewState === 'expired-message' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'expired-message'; }}
                >Expired (message)</s-button>
                <s-button
                  size="slim"
                  variant=${this._previewState === 'expired-hidden' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'expired-hidden'; }}
                >Expired (hidden)</s-button>
              </div>
              <div class="preview-frame ${this._saveFlash ? 'preview-pulse' : ''}">
                ${this._renderPreview()}
              </div>
            </s-section>
          </div>
        </div>
      </s-page>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hype-countdown-page': CountdownPage;
  }
}
